import { useLayoutEffect, useState } from 'react'
import { VIEWPORT_PADDING_PX } from './fitMenuToViewport'

const PARENT_MENU_SELECTOR = '.popover-menu-dropdown'

const clamp = (value, min, max) => Math.max(min, Math.min(value, max))

/**
 * Viewport position (`top`/`left` for `position: fixed`) of a sub-menu beside its menu item: on the
 * right when it fits there, otherwise on the side with more room, then kept inside the viewport.
 */
export function getSubMenuPosition(itemRect, subMenuSize, viewportSize) {
  const spaceOnRight = viewportSize.width - itemRect.right - VIEWPORT_PADDING_PX
  const spaceOnLeft = itemRect.left - VIEWPORT_PADDING_PX
  const opensLeft = spaceOnRight < subMenuSize.width && spaceOnLeft > spaceOnRight
  const preferredLeft = opensLeft ? itemRect.left - subMenuSize.width : itemRect.right

  return {
    top: clamp(itemRect.top, VIEWPORT_PADDING_PX, viewportSize.height - subMenuSize.height - VIEWPORT_PADDING_PX),
    left: clamp(preferredLeft, VIEWPORT_PADDING_PX, viewportSize.width - subMenuSize.width - VIEWPORT_PADDING_PX),
  }
}

/** True when the item has scrolled out of its parent menu, which scrolls when taller than the viewport. */
function isScrolledOutOfMenu(itemRect, menuRect) {
  return itemRect.top > menuRect.bottom || itemRect.bottom < menuRect.top
}

/**
 * Keeps a sub-menu portaled out of its parent menu (so the scrolling menu can't clip it) placed
 * beside its item while the page or the menu scrolls, and calls `onItemHidden` once the item scrolls
 * out of the menu so the sub-menu isn't left behind on its own. Returns `null` until measured.
 */
export function useSubMenuPosition(isOpen, menuItemRef, subMenuRef, onItemHidden) {
  const [position, setPosition] = useState(null)

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null)
      return undefined
    }

    const updatePosition = () => {
      const menuItem = menuItemRef.current
      const subMenuElement = subMenuRef.current
      if (!menuItem || !subMenuElement) {
        return
      }
      const itemRect = menuItem.getBoundingClientRect()
      const menuRect = menuItem.closest(PARENT_MENU_SELECTOR)?.getBoundingClientRect()
      if (menuRect && isScrolledOutOfMenu(itemRect, menuRect)) {
        onItemHidden()
        return
      }
      setPosition(
        getSubMenuPosition(
          itemRect,
          { width: subMenuElement.offsetWidth, height: subMenuElement.offsetHeight },
          { width: document.documentElement.clientWidth, height: window.innerHeight },
        ),
      )
    }

    updatePosition()
    // Capture so scrolling the parent menu (or any other container) keeps the sub-menu attached.
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen, menuItemRef, subMenuRef, onItemHidden])

  return position
}
